import { ShouldSegmentInsert } from '@com/components/timeline-panel/components/props'
import {
  getBeforeGaps,
  resolveSegmentFramesIfGap,
} from '@com/components/timeline-panel/utils/segment'
import {
  getConflictSegment,
  InsertResult,
} from '@com/components/timeline-panel/utils/track-insert-position'
import {
  IvSegmentType,
  IvTrackType,
} from '@ps/store/interactive-video/timeline/enums'
import {
  IvComponentSegment,
  IvSegment,
  IvSegmentAudio,
  IvSegmentHand,
  IvSegmentImage,
  IvSegmentText,
  IvTrack,
  IvTrackIndex,
} from '@ps/store/interactive-video/timeline/types'
import {
  getIvRelativedSegments,
  getRelativedIv,
  isSegmentAudio,
  isSegmentIv,
  isSegmentVideo,
} from '@ps/store/interactive-video/timeline/utiles'
import { findVideoSegmentFromTracks, getScopedStart } from './segment'
import {
  getTimelineBySceneUuid,
  setTimeline,
} from '@ps/store/interactive-video/timeline/timeline'
import { DragendCallbackPayload } from '@com/components/timeline-panel/components/track'
import { findSceneSegment } from '@ps/store/interactive-video/timeline/utiles/scene'
import {
  copyTrackItem,
  effectInsertResult,
} from '@com/components/timeline-panel/utils/track-action'
import {
  addIvSegment,
  insertBeforeIvTrack,
  removeIvSegment,
} from '@ps/store/interactive-video/timeline/utiles/track-action'
import Vue from 'vue'
import { px2frame } from '@com/components/timeline-panel/utils/frame'
import { TrackFrameUnit } from '@com/components/timeline-panel/types'
import { resolveGapFrames } from '@com/components/timeline/shared'

type SegmentOthers = IvSegmentText | IvSegmentImage | IvSegmentHand

let prevThroughedIv: IvComponentSegment | null = null

const fixConflict = (
  returnInsertResult: InsertResult,
  segment: IvSegment,
): void => {
  // 检查冲突
  if (returnInsertResult.position === 'middle') {
    const conflictSegment = getConflictSegment(
      {
        ...segment,
        start: returnInsertResult.start,
        frames: returnInsertResult.frames,
      },
      returnInsertResult.track,
    )

    if (conflictSegment) {
      returnInsertResult.position = 'top'
    }
  }
}
const shouldInsertSegmentOthers: ShouldSegmentInsert<
  InsertResult,
  SegmentOthers,
  IvTrack
> = (result, segment, payload) => {
  const targetVideoSegment = findVideoSegmentFromTracks(
    segment.sceneUuid,
    payload.tracks,
  )

  if (!targetVideoSegment) {
    return null
  }

  const targetIv = getRelativedIv(segment, payload.tracks)
  if (targetIv && targetIv.type === IvSegmentType.Freeze) {
    result.frames = targetIv.frames

    // 目标视频结束位置，确保附属元件长度不超过视频结束位置
    const scopedEnd = targetVideoSegment.start + targetVideoSegment.frames
    // 解决returnInsertResult.frames + start > targetVideoSegment.frames的问题
    if (result.start + result.frames > scopedEnd) {
      result.start = scopedEnd - result.frames
    }
  }

  const videoTrack = payload.tracks.find(
    (t) => t.id === targetVideoSegment.trackId,
  )
  const scopedStart = getScopedStart(targetVideoSegment, {
    ...segment,
    start: result.start,
  })

  let returnInsertResult: typeof result = {
    ...result,
    start: scopedStart,
  }

  const scopedTracks = payload.tracks.filter((t) =>
    [IvTrackType.Hand, IvTrackType.Image, IvTrackType.Text].includes(t.type),
  )
  const inScoped = scopedTracks.some((t) => t.id === result.track.id)
  if (!inScoped && result.position !== 'middle') {
    // 互动轨道之下
    if (result.track.type === IvTrackType.Iv) {
      returnInsertResult = {
        ...returnInsertResult,
        start: scopedStart,
        position: 'bottom',
      }
    } else {
      // 视频轨道之上
      returnInsertResult = {
        ...returnInsertResult,
        start: scopedStart,
        track: videoTrack,
        position: 'top',
      }
    }
  }
  // 附属元件时
  // 支持拖互动元件范围，作为非附属元件

  // 非附属元件时
  // 拖拽元件的鼠标位置进入互动元件范围，自动focus互动元件，并计算出在作为附属元件的ghost样式
  // 拖拽目标元件是互动定格元件，设置元件时长设置为1帧，若是其他互动元件等于互动元件时长

  const ivComponentSegments = payload.tracks
    .reduce<IvComponentSegment[]>((segments, t) => {
      if (t.type === IvTrackType.Iv) {
        segments.push(
          ...t.segments.filter((s) => s.sceneUuid === segment.sceneUuid),
        )
      }
      return segments
    }, [])
    .sort((a, b) => a.start - b.start)

  const xFrame = px2frame(payload.x, payload.frameWidth)
  const currentScopedBeforeGapFrames = getBeforeGaps(
    targetVideoSegment.start,
    payload.gaps,
  )

  const findThroughIv = (xFrame: TrackFrameUnit) => {
    let beforeGapFrames = currentScopedBeforeGapFrames
    for (let index = 0; index < ivComponentSegments.length; index++) {
      const s = ivComponentSegments[index]!

      if (
        xFrame >= s.start + beforeGapFrames &&
        xFrame < s.start + s.frames + beforeGapFrames
      ) {
        return s
      }

      if (s.is === 'gap') {
        beforeGapFrames += resolveGapFrames({
          in: s.start,
          frames: s.frames,
        })
      }
    }
  }

  const throughIv = findThroughIv(xFrame)

  if (throughIv) {
    // 成为附属元件:
    // 1. 设置元件时长
    // 2. 设置元件进场时间
    returnInsertResult = {
      ...returnInsertResult,
      start: throughIv.start,
      frames: resolveSegmentFramesIfGap(throughIv),
    }

    // active the through iv segment.
    if (!throughIv.styles) {
      Vue.set(throughIv, 'styles', [])
    }
    throughIv.styles?.push('active')
    prevThroughedIv = throughIv
  } else {
    if (prevThroughedIv) {
      prevThroughedIv.styles = prevThroughedIv?.styles?.filter(
        (style) => style !== 'active',
      )
      prevThroughedIv = null
    }
  }

  fixConflict(returnInsertResult, segment)

  return returnInsertResult
}

const shouldInsertSegmentAudio: ShouldSegmentInsert<
  InsertResult,
  IvSegmentAudio,
  IvTrack
> = (result, segment, payload) => {
  const targetVideoSegment = findVideoSegmentFromTracks(
    segment.sceneUuid,
    payload.tracks,
  )

  if (!targetVideoSegment) {
    return null
  }
  const videoTrack = payload.tracks.find(
    (t) => t.id === targetVideoSegment.trackId,
  )!

  const scopedStart = getScopedStart(targetVideoSegment, {
    ...segment,
    start: result.start,
  })

  const returnInsertResult: typeof result = {
    ...result,
    start: scopedStart,
  }

  if (result.track.type !== IvTrackType.Audio) {
    returnInsertResult.track = videoTrack
    returnInsertResult.position = 'bottom'
  }

  fixConflict(returnInsertResult, segment)

  return returnInsertResult
}

export function findIvSegmentInsertableStart(
  segment: IvComponentSegment,
  tracks: IvTrack[],
): IvComponentSegment['start'] | -1 {
  const targetVideoSegment = findVideoSegmentFromTracks(
    segment.sceneUuid,
    tracks,
  )
  if (!targetVideoSegment) {
    return -1
  }

  const cursorStart = segment.start
  const scopedStart = getScopedStart(targetVideoSegment, {
    ...segment,
    start: cursorStart,
  })

  const targetTrack = tracks.find((t) => t.type === IvTrackType.Iv)
  // 若互动轨道不存在，则符合范围内的都可以
  if (!targetTrack) return scopedStart

  const conflictSegment = getConflictSegment(
    { ...segment, start: scopedStart },
    targetTrack,
  )
  if (!conflictSegment) {
    return scopedStart
  }
  console.log('aaa conflictSegment', conflictSegment)
  const sortedSegments =
    targetTrack.segments
      ?.filter((s) => s.id !== segment.id)
      .sort((a, b) => a.start - b.start) ?? []

  const conflictSegmentIndex = sortedSegments?.findIndex(
    (s) => s.id === conflictSegment.id,
  )

  const leftSegments = sortedSegments.slice(0, conflictSegmentIndex)
  const _leftStart = leftSegments.reverse().reduce((start) => {
    const _conflictSegment = getConflictSegment(
      { ...segment, start },
      targetTrack,
    )
    if (_conflictSegment) {
      return _conflictSegment.start - resolveSegmentFramesIfGap(segment)
    }

    return start
  }, conflictSegment.start - resolveSegmentFramesIfGap(segment))

  const rightSegments = sortedSegments.slice(conflictSegmentIndex + 1)
  const _rightStart = rightSegments.reduce((start) => {
    const _conflictSegment = getConflictSegment(
      { ...segment, start },
      targetTrack,
    )
    if (_conflictSegment) {
      return _conflictSegment.start + resolveSegmentFramesIfGap(segment)
    }

    return start
  }, conflictSegment.start + resolveSegmentFramesIfGap(conflictSegment))

  const closerStart = [_leftStart, _rightStart]
    .map((_start) =>
      getScopedStart(targetVideoSegment, { ...segment, start: _start }),
    )
    .filter((_start) => {
      const _conflictSegment = getConflictSegment(
        { ...segment, start: _start },
        targetTrack,
      )
      return !_conflictSegment
    })
    .reduce((rStart, start) => {
      if (rStart === -1) {
        return start
      }

      if (Math.abs(start - cursorStart) < Math.abs(rStart - cursorStart)) {
        return start
      }

      return rStart
    }, -1)

  return closerStart
}

const shouldInsertSegmentIv: ShouldSegmentInsert<
  InsertResult,
  IvComponentSegment,
  IvTrack
> = (result, segment, payload) => {
  const fallbackStart = segment.start
  // iv 互动元件，仅在互动轨道上，置顶
  const targetTrack = payload.tracks.find((t) => t.id === segment.trackId)

  // 若互动轨道不存在，则不处理
  if (!targetTrack) return null
  const returnInsertResult = (start: InsertResult['start']) => {
    return {
      ...result,
      position: 'middle',
      start,
      track: targetTrack,
    }
  }

  const v = findIvSegmentInsertableStart(
    { ...segment, start: result.start },
    payload.tracks,
  )

  return returnInsertResult(v === -1 ? fallbackStart : v)
}

export const shouldInsert: ShouldSegmentInsert = (result, target, payload) => {
  const segment = target as unknown as IvSegment

  if (isSegmentVideo(segment)) {
    // 视频元件不允许拖拽
    return null
  } else if (isSegmentIv(segment)) {
    return shouldInsertSegmentIv(result, segment, payload as any)
  } else if (isSegmentAudio(segment)) {
    return shouldInsertSegmentAudio(result, segment, payload as any)
  }

  return shouldInsertSegmentOthers(result, segment, payload as any)
}

export function transformToMirrorScene<
  T extends DragendCallbackPayload['result'],
>(
  mergedTracksSegment: IvSegment,
  result: T,
  beforeApplyInsertResultmergedTracks: IvTrack[],
): void {
  const mergedTracks = beforeApplyInsertResultmergedTracks
  if (isSegmentVideo(mergedTracksSegment)) return

  const sceneUuid = mergedTracksSegment.sceneUuid
  const tracks = getTimelineBySceneUuid(sceneUuid)

  // 描述文件里面的元件
  const mirrorTargetSegment = findSceneSegment<typeof mergedTracksSegment>(
    mergedTracksSegment.sceneUuid,
    mergedTracksSegment.id,
    {
      fromTracks: tracks,
    },
  )

  if (!mirrorTargetSegment) {
    return
  }

  const ir = transformToMirrorSceneResult(result, {
    sceneTracks: tracks,
    target: mergedTracksSegment,
    mergedTracks,
  })

  console.log('aaa transformToMirrorSceneResult', {
    before: result,
    after: ir,
  })
  if (!ir) {
    return
  }

  const relativedSegments = isSegmentIv(mirrorTargetSegment)
    ? getIvRelativedSegments(mirrorTargetSegment, tracks)
    : []

  effectInsertResult(mirrorTargetSegment, ir, tracks as any, {
    removeSegment(segment, tracks, options) {
      removeIvSegment(segment as any, tracks as any, options)
    },
    addSegment(segment, track) {
      addIvSegment(segment as any, track as any)
    },
  })

  if (isSegmentIv(mirrorTargetSegment)) {
    relativedSegments.forEach((s) => {
      s.selected = false
      s.start = mirrorTargetSegment.start
      const track = tracks.find((t) => t.id === s.trackId)
      if (track && getConflictSegment(s, track)) {
        removeIvSegment(s, tracks, {
          removeTrackIfEmpty: false,
        })
        const newTrack = copyTrackItem(track)
        addIvSegment(s, newTrack as any)
        insertBeforeIvTrack(newTrack, track, tracks)
      }
    })
  }

  setTimeline(sceneUuid, tracks)
}

function transformToMirrorSceneResult<
  T extends DragendCallbackPayload['result'],
>(
  result: T,
  options: {
    target: IvSegment
    sceneTracks: IvTrack[]
    mergedTracks: IvTrack[]
  },
): T | null {
  if (!result) return result

  const { track } = result
  const { target: mergedTracksTarget, mergedTracks, sceneTracks } = options
  const sceneUuid = mergedTracksTarget.sceneUuid
  const mergedVideoSegment = findVideoSegmentFromTracks(sceneUuid, mergedTracks)

  const targetTrackIndexInMergedTracks = mergedTracks.findIndex(
    (t) => t.id === track.id,
  )

  if (!mergedVideoSegment || targetTrackIndexInMergedTracks === -1) {
    return null
  }

  const targetTrack = mergedTracks[targetTrackIndexInMergedTracks]
  const hasSegmentTrack = (t: IvTrack): boolean => {
    return Boolean(
      t.segments.filter(
        (s) => s.sceneUuid === sceneUuid && s.id !== mergedTracksTarget.id,
      ).length,
    )
  }
  const findSceneTrack = (mergedTargetTrack: IvTrack) => {
    const segment = mergedTargetTrack.segments.find(
      (s) => s.sceneUuid === sceneUuid && s.id !== mergedTracksTarget.id,
    )

    const targetSceneTrack = segment
      ? sceneTracks.find((t) => t.segments.some((s) => s.id === segment.id))
      : undefined

    return targetSceneTrack
  }

  const _returnResult = (
    _track: IvTrack | null,
    _position?: InsertResult['position'],
  ) => {
    if (!_track) return null

    return {
      ...result,
      track: _track,
      position: _position || result.position,
      start: result.start - mergedVideoSegment.start,
    }
  }

  // 如果目标轨道就在sceneTracks里面，直接返回
  const sceneTrack = targetTrack ? findSceneTrack(targetTrack) : undefined
  if (sceneTrack) {
    return _returnResult(sceneTrack)
  }

  const findHasSegmentTrack = (startIndex: IvTrackIndex, step: 1 | -1) => {
    let index = startIndex + step
    while (mergedTracks[index]) {
      const t = mergedTracks[index]

      if (t && hasSegmentTrack(t)) {
        return t
      }

      index += step
    }
  }

  const aboveTrack = findHasSegmentTrack(targetTrackIndexInMergedTracks, -1)
  const aboveSceneTrack = aboveTrack ? findSceneTrack(aboveTrack) : undefined
  if (aboveSceneTrack) {
    return _returnResult(aboveSceneTrack, 'bottom')
  }

  const belowTrack = findHasSegmentTrack(targetTrackIndexInMergedTracks, 1)

  const belowSceneTrack = belowTrack ? findSceneTrack(belowTrack) : undefined
  if (belowSceneTrack) {
    return _returnResult(belowSceneTrack, 'top')
  }

  return null
}
